import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, FormGroup, Label, FormFeedback, FormText, Alert, Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from '../../common/Select';

const months = [
  { value: '01', label: 'Jan' },
  { value: '02', label: 'Feb' },
  { value: '03', label: 'Mar' },
  { value: '04', label: 'Apr' },
  { value: '05', label: 'May' },
  { value: '06', label: 'Jun' },
  { value: '07', label: 'Jul' },
  { value: '08', label: 'Aug' },
  { value: '09', label: 'Sep' },
  { value: '10', label: 'Oct' },
  { value: '11', label: 'Nov' },
  { value: '12', label: 'Dec' }
];

function getDaysInMonth(year, month) {
  if (!year || !month) return 31;
  return new Date(parseInt(year), parseInt(month), 0).getDate();
}

function generateYearOptions() {
  const currentYear = new Date().getFullYear();
  const options = [];
  for (let y = currentYear; y >= currentYear - 10; y--) {
    options.push({ value: String(y), label: String(y) });
  }
  return options;
}

function generateDayOptions(year, month) {
  const maxDay = getDaysInMonth(year, month);
  const options = [];
  for (let d = 1; d <= maxDay; d++) {
    const val = String(d).padStart(2, '0');
    options.push({ value: val, label: val });
  }
  return options;
}

function parseDateValue(dateStr) {
  if (!dateStr) return { year: null, month: null, day: null };
  const parts = dateStr.split('-');
  if (parts.length !== 3) return { year: null, month: null, day: null };
  return { year: parts[0], month: parts[1], day: parts[2] };
}

function buildDateValue(year, month, day) {
  if (!year || !month || !day) return '';
  return `${year}-${month}-${day}`;
}

const selectTheme = theme => ({
  ...theme,
  borderRadius: 5,
  colors: { ...theme.colors, primary25: '#253746', primary: '#bd472a' }
});

export default function VendorDaysBack({ bugsDateBack, vendorId, setBugsDateBack, isSubmitted }) {
  const calendarRef = useRef(null);
  const { year, month, day } = parseDateValue(bugsDateBack);

  const yearOptions = generateYearOptions();
  const dayOptions = generateDayOptions(year, month);

  const handleYearChange = selected => {
    if (!selected) return setBugsDateBack('');
    const newDay = day && parseInt(day) > getDaysInMonth(selected.value, month) ? '01' : day;
    setBugsDateBack(buildDateValue(selected.value, month || '01', newDay || '01'));
  };

  const handleMonthChange = selected => {
    if (!selected) return setBugsDateBack('');
    const newDay = day && parseInt(day) > getDaysInMonth(year, selected.value) ? '01' : day;
    setBugsDateBack(buildDateValue(year || String(new Date().getFullYear()), selected.value, newDay || '01'));
  };

  const handleDayChange = selected => {
    if (!selected) return setBugsDateBack('');
    setBugsDateBack(buildDateValue(year || String(new Date().getFullYear()), month || '01', selected.value));
  };

  const handleCalendarChange = e => {
    if (e.target.value) {
      setBugsDateBack(e.target.value);
    }
  };

  const openCalendar = () => {
    if (calendarRef.current) {
      calendarRef.current.showPicker?.();
      calendarRef.current.click();
    }
  };

  const sixMonthsAgo =
    vendorId === 'msft365' || vendorId === 'msftserver'
      ? 'This service can get bugs updated in the last 6 months.'
      : null;

  const today = new Date().toISOString().split('T')[0];

  return (
    <FormGroup>
      <Label>
        Look for bugs from<span style={{ color: '#bd472a' }}>*</span>
      </Label>
      <Row className="align-items-center">
        <Col>
          <Row>
            <Col>
              <Select
                value={months.find(m => m.value === month) || null}
                options={months}
                onChange={handleMonthChange}
                placeholder="Month"
                theme={selectTheme}
                isClearable={false}
                data-testid="days-back-month"
              />
            </Col>
            <Col>
              <Select
                value={dayOptions.find(d => d.value === day) || null}
                options={dayOptions}
                onChange={handleDayChange}
                placeholder="Day"
                theme={selectTheme}
                isClearable={false}
                data-testid="days-back-day"
              />
            </Col>
            <Col>
              <Select
                value={yearOptions.find(y => y.value === year) || null}
                options={yearOptions}
                onChange={handleYearChange}
                placeholder="Year"
                theme={selectTheme}
                isClearable={false}
                data-testid="days-back-year"
              />
            </Col>
            <Col xs="auto" className="px-0 d-flex">
              <Button
                color="falcon-default"
                className="d-flex align-items-center justify-content-center w-100"
                onClick={openCalendar}
                title="Open calendar"
                style={{ height: '38px' }}
              >
                <FontAwesomeIcon icon="calendar-alt" />
              </Button>
              <input
                ref={calendarRef}
                type="date"
                value={bugsDateBack || ''}
                max={today}
                onChange={handleCalendarChange}
                style={{
                  position: 'absolute',
                  opacity: 0,
                  width: 0,
                  height: 0,
                  overflow: 'hidden',
                  pointerEvents: 'none'
                }}
                tabIndex={-1}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      {sixMonthsAgo ? (
        <FormText color="muted">{sixMonthsAgo}</FormText>
      ) : (
        <FormText color="muted">Eg. Look for bugs from Jan 01, 2025 onwards.</FormText>
      )}
      <FormFeedback className={!bugsDateBack && isSubmitted ? 'd-block' : ''}>
        <Alert color="danger" fade={false}>
          Please specify a date.
        </Alert>
      </FormFeedback>
    </FormGroup>
  );
}

VendorDaysBack.propTypes = {
  bugsDateBack: PropTypes.string,
  setBugsDateBack: PropTypes.func,
  isSubmitted: PropTypes.bool,
  vendorId: PropTypes.string
};
